/**
 * One business trip of the Fahrtenbuch: where, why, how far and how long - with what it is worth
 * shown while it is typed (lib/at/travel.ts). The Kilometergeldverordnung asks for date, odometer
 * reading, kilometres, start, destination and purpose, so the form asks for exactly those.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useDeleteTrip, useSaveTrip } from "@/hooks/use-trips";
import type { Trip, TripFields } from "@/lib/api/trips";
import { tripInput } from "@/lib/api/trips";
import { kmBefore, mileageRate, tripAmounts, type TripInput, type Vehicle } from "@/lib/at/travel";
import { useWorkspace } from "@/lib/session/workspace-context";
import { formatMoney, parseDecimal } from "@/lib/utils/format";

const VEHICLES = ["car", "motorcycle", "bicycle", "none"] as const;
const day = (value: string | undefined) => (value ?? "").slice(0, 10);
const number = (value: number | undefined) => (value ? String(value).replace(".", ",") : "");

export function TripDialog({
  open,
  onOpenChange,
  trip,
  trips,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The trip to edit; a new one when absent. */
  trip?: Trip | null;
  /** Every trip, for the kilometres the year's cap already used. */
  trips: Trip[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {open && (
          <TripForm
            key={trip?.id ?? "new"}
            trip={trip ?? null}
            trips={trips}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TripForm({
  trip,
  trips,
  onDone,
}: {
  trip: Trip | null;
  trips: Trip[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const save = useSaveTrip(workspace?.id ?? "");
  const remove = useDeleteTrip();

  const count = z.string().refine((value) => /^\d{0,3}$/.test(value.trim()), t("trips.invalid"));
  const time = z
    .string()
    .refine((value) => value === "" || /^([01]\d|2[0-3]):[0-5]\d$/.test(value), t("trips.invalid"));
  const schema = z
    .object({
      date: z.string().min(10, t("trips.dateRequired")),
      endDate: z.string(),
      departureTime: time,
      returnTime: time,
      origin: z.string().max(200),
      destination: z.string().trim().min(1, t("trips.destinationRequired")).max(200),
      purpose: z.string().trim().min(1, t("trips.purposeRequired")).max(300),
      vehicle: z.enum(VEHICLES),
      odometerStart: z.string(),
      odometerEnd: z.string(),
      distanceKm: z.string(),
      perDiem: z.boolean(),
      meals: count,
      nights: count,
      notes: z.string().max(1000),
    })
    .refine((values) => !values.endDate || values.endDate >= values.date, {
      path: ["endDate"],
      message: t("trips.endBeforeStart"),
    })
    .refine((values) => !values.perDiem || (values.departureTime && values.returnTime), {
      path: ["returnTime"],
      message: t("trips.timesRequired"),
    });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: day(trip?.date) || new Date().toISOString().slice(0, 10),
      endDate: day(trip?.endDate),
      departureTime: trip?.departureTime ?? "",
      returnTime: trip?.returnTime ?? "",
      origin: trip?.origin ?? "",
      destination: trip?.destination ?? "",
      purpose: trip?.purpose ?? "",
      vehicle: trip?.vehicle ?? "car",
      odometerStart: number(trip?.odometerStart),
      odometerEnd: number(trip?.odometerEnd),
      distanceKm: number(trip?.distanceKm),
      perDiem: trip?.perDiem ?? false,
      meals: trip?.meals ? String(trip.meals) : "",
      nights: trip?.nights ? String(trip.nights) : "",
      notes: trip?.notes ?? "",
    },
  });

  /** The kilometres: as typed, or the odometer difference when only that is given. */
  const distance = (values: Values) => {
    const typed = parseDecimal(values.distanceKm);
    if (typed !== null && typed !== undefined) return Math.max(0, typed);
    const start = parseDecimal(values.odometerStart) ?? 0;
    const end = parseDecimal(values.odometerEnd) ?? 0;
    return end > start ? end - start : 0;
  };
  const toInput = (values: Values): TripInput & { id: string } => ({
    id: trip?.id ?? "~new",
    date: values.date,
    endDate: values.endDate || undefined,
    departureTime: values.departureTime || undefined,
    returnTime: values.returnTime || undefined,
    vehicle: values.vehicle as Vehicle,
    distanceKm: values.vehicle === "none" ? 0 : distance(values),
    perDiem: values.perDiem,
    meals: Number(values.meals) || 0,
    nights: Number(values.nights) || 0,
  });

  const watched = form.watch();
  const input = toInput(watched);
  const amounts = tripAmounts(input, kmBefore(input, trips.map(tripInput)));
  const vehicle = watched.vehicle;

  async function submit(values: Values) {
    const parsed = toInput(values);
    const fields: TripFields = {
      date: parsed.date,
      endDate: values.endDate,
      departureTime: values.departureTime,
      returnTime: values.returnTime,
      origin: values.origin.trim(),
      destination: values.destination.trim(),
      purpose: values.purpose.trim(),
      vehicle: values.vehicle,
      distanceKm: parsed.distanceKm ?? 0,
      odometerStart: parseDecimal(values.odometerStart) ?? 0,
      odometerEnd: parseDecimal(values.odometerEnd) ?? 0,
      perDiem: values.perDiem,
      meals: parsed.meals ?? 0,
      nights: parsed.nights ?? 0,
      notes: values.notes.trim(),
    };
    try {
      await save.mutateAsync({ id: trip?.id, fields });
      toast.success(trip ? t("trips.saved") : t("trips.created"));
      onDone();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("trips.failed"));
    }
  }

  const text = (name: "origin" | "destination" | "purpose", label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input disabled={!canWrite} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
  const field = (
    name:
      | "date"
      | "endDate"
      | "departureTime"
      | "returnTime"
      | "odometerStart"
      | "odometerEnd"
      | "distanceKm"
      | "meals"
      | "nights",
    label: string,
    type: "date" | "time" | "number",
    hint?: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field: input }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {type === "number" ? (
              <Input inputMode="decimal" className="num" disabled={!canWrite} {...input} />
            ) : (
              <Input type={type} disabled={!canWrite} {...input} />
            )}
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{trip ? trip.destination : t("trips.new")}</DialogTitle>
        <DialogDescription>{t("trips.dialogHint")}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form
          id="trip-form"
          className="grid gap-4"
          data-testid="trip-form"
          onSubmit={form.handleSubmit(submit)}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {text("origin", t("trips.origin"))}
            {text("destination", t("trips.destination"))}
          </div>
          {text("purpose", t("trips.purpose"))}
          <div className="grid gap-4 sm:grid-cols-4">
            {field("date", t("trips.date"), "date")}
            {field("departureTime", t("trips.departure"), "time")}
            {field("endDate", t("trips.endDate"), "date")}
            {field("returnTime", t("trips.return"), "time")}
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <FormField
              control={form.control}
              name="vehicle"
              render={({ field: select }) => (
                <FormItem>
                  <FormLabel>{t("trips.vehicle")}</FormLabel>
                  <Select value={select.value} onValueChange={select.onChange} disabled={!canWrite}>
                    <FormControl>
                      <SelectTrigger data-testid="trip-vehicle">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VEHICLES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`trips.vehicles.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {vehicle !== "none" && (
              <>
                {field("odometerStart", t("trips.odometerStart"), "number")}
                {field("odometerEnd", t("trips.odometerEnd"), "number")}
                {field(
                  "distanceKm",
                  t("trips.distance"),
                  "number",
                  t("trips.rate", {
                    rate: formatMoney(mileageRate(vehicle, watched.date || "2026-01-01") / 100),
                  }),
                )}
              </>
            )}
          </div>
          <FormField
            control={form.control}
            name="perDiem"
            render={({ field: check }) => (
              <FormItem className="flex items-start gap-3 rounded-md border p-3">
                <FormControl>
                  <Checkbox
                    checked={check.value}
                    onCheckedChange={(value) => check.onChange(value === true)}
                    disabled={!canWrite}
                    data-testid="trip-per-diem"
                  />
                </FormControl>
                <div className="space-y-1">
                  <FormLabel>{t("trips.perDiem")}</FormLabel>
                  <FormDescription>{t("trips.perDiemHint")}</FormDescription>
                </div>
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {field("meals", t("trips.meals"), "number", t("trips.mealsHint"))}
            {field("nights", t("trips.nights"), "number", t("trips.nightsHint"))}
          </div>
          <FormField
            control={form.control}
            name="notes"
            render={({ field: notes }) => (
              <FormItem>
                <FormLabel>{t("trips.notes")}</FormLabel>
                <FormControl>
                  <Textarea rows={2} disabled={!canWrite} {...notes} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <dl
        className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted px-3 py-2 text-sm sm:grid-cols-4"
        data-testid="trip-amounts"
      >
        <dt className="text-muted-foreground">{t("trips.mileage")}</dt>
        <dd className="text-right num sm:text-left">{formatMoney(amounts.mileage / 100)}</dd>
        <dt className="text-muted-foreground">{t("trips.perDiemAmount")}</dt>
        <dd className="text-right num sm:text-left">{formatMoney(amounts.perDiem / 100)}</dd>
        <dt className="text-muted-foreground">{t("trips.lodging")}</dt>
        <dd className="text-right num sm:text-left">{formatMoney(amounts.lodging / 100)}</dd>
        <dt className="font-medium">{t("trips.total")}</dt>
        <dd className="text-right font-semibold num sm:text-left" data-testid="trip-total">
          {formatMoney(amounts.total / 100)}
        </dd>
      </dl>
      {input.distanceKm !== undefined && amounts.paidKm < input.distanceKm && (
        <p className="text-sm text-warning" role="alert">
          {t("trips.overCap", { km: amounts.paidKm })}
        </p>
      )}

      {canWrite && (
        <DialogFooter className="gap-2 sm:justify-between">
          {trip ? (
            <Button
              variant="ghost"
              data-testid="delete-trip"
              disabled={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(trip.id);
                toast.success(t("trips.deleted"));
                onDone();
              }}
            >
              <Trash2 /> {t("common.delete")}
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" form="trip-form" disabled={save.isPending} data-testid="save-trip">
            {t("common.save")}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}
